// AnimationController.js - Simplified animation orchestration (100 lines!)
import { getAnimationContainer } from '@containers/MapContainer/AnimationLayer';
import { calculateMSBasedOnTicks } from '@utils/animationTiming';
import useMapStore from '@store/useMapStore';
import './AnimationUtilities.css';

class AnimationController {
  // Vite's import.meta.glob - auto-generates registry of all animation files at build time
  static animationModules = import.meta.glob('./animations/*Animation.js');
  static defaultModule = import.meta.glob('./DefaultAnimation.js');

  constructor(rqItem, onComplete) {
    this.animations = rqItem?.animations;
    this.onComplete = onComplete;
    this.cancelled = false;
    this.startTimeouts = [];  // For delayed starts
    this.destroyTimeouts = new Map();  // For each animation's destruction
    this.activeAnimations = new Map();
  }

  start() {
    if (this.cancelled) return;

    // Log all animations we're about to process
    //console.log('[AnimationController] Starting with animations:', this.animations.map(a => a.animation_name));

    if( this.animations.length === 0 ) {
      console.warn('[AnimationController] No animations defined, finishing immediately');
      this.finish();
      return;
    }

    const container = getAnimationContainer();
    if (!container ) {
      // No visuals possible, just wait and complete
      console.warn('[AnimationController] No container to run');
      this.finish();
      return;
    }

    // Validate and schedule animations with detailed error reporting
    const { validAnimations, invalidAnimations } = this.validateAnimations(this.animations);

    // Log invalid animations with specific reasons
    if (invalidAnimations.length > 0) {
      console.group('[AnimationController] ⚠️ Invalid Animations Detected');
      invalidAnimations.forEach(({ animation, errors }) => {
        console.warn(`Animation validation failed:`, {
          received: animation,
          missing_fields: errors,
          animation_name: animation?.animation_name || 'MISSING',
          has_duration: animation?.duration !== undefined,
          has_delay: animation?.delay !== undefined
        });
      });
      console.groupEnd();
    }

    if (validAnimations.length === 0) {
      console.error('[AnimationController] ❌ No valid animations to run', {
        total_received: this.animations.length,
        all_invalid: invalidAnimations.length,
        details: 'All animations are missing required fields (animation_name, duration, delay)'
      });
      this.waitAndFinish();
      return;
    }

    // Log what we're actually running
    //console.log(`[AnimationController] ✅ Running ${validAnimations.length}/${this.animations.length} valid animations`);
    if (invalidAnimations.length > 0) {
      console.warn(`[AnimationController] ⚠️ Skipped ${invalidAnimations.length} invalid animations`);
    }

    // Calculate stacking offsets for text animations at same position and delay
    this.calculateStackingOffsets(validAnimations);

    // Schedule each animation at its delay
    validAnimations.forEach(anim => {
      const delayMS = calculateMSBasedOnTicks(anim.delay);
      //console.log(`[AnimationController] Scheduling ${anim.animation_name} to start in ${anim.delay} ticks (${delayMS}ms)`);

      const timeout = setTimeout(() => {
        if (!this.cancelled) {
          //console.log(`[AnimationController] Starting ${anim.animation_name} animation with timeout of ${anim.duration} ticks`);
          this.runAnimation(anim, container);
        }
      }, delayMS);

      this.startTimeouts.push(timeout);
    });

    // Calculate when all animations should be done
    const animationTimings = validAnimations.map(a => ({
      name: a.animation_name,
      delay: a.delay,
      duration: a.duration,
      endTime: (a.delay) + (a.duration)
    }));

    //console.log('[AnimationController] Animation timings:', animationTimings);

    const maxEndTime = Math.max(...animationTimings.map(a => a.endTime));

    //console.log('[AnimationController] Max end time:', maxEndTime, 'ticks =', calculateMSBasedOnTicks(maxEndTime), 'ms');

    // Set a timeout for completion
    const completeTimeout = setTimeout(() => {
      if (!this.cancelled) {
        //console.info('[AnimationController] Complete timeout triggered, finishing...');
        this.finish();
      }
    }, calculateMSBasedOnTicks(maxEndTime));

    this.startTimeouts.push(completeTimeout);
  }

  async runAnimation(animData, container) {
    const AnimationClass = await this.loadAnimationClass(animData);
    if (!AnimationClass){
      console.error(`[AnimationController] ❌ Failed to load animation class`, {
        animation_name: animData.animation_name,
        category: animData.category,
        attempted_paths: [
          `./animations/${this.toPascalCase(animData.animation_name)}Animation.js`,
          animData.category ? `./animations/Category${animData.category.charAt(0).toUpperCase() + animData.category.slice(1)}Animation.js` : null,
          './DefaultAnimation.js'
        ].filter(Boolean),
        suggestion: 'Check if animation class exists or if DefaultAnimation.js is available'
      });
      return;
    }

    const id = `${animData.animation_name}_${Date.now()}`;

    // Pass flat data structure
    const instance = new AnimationClass({
      ...animData,  // Spread all animation properties flat
      container: container,
      durationMS: calculateMSBasedOnTicks(animData.duration),
      delayMS: calculateMSBasedOnTicks(animData.delay)
    });

    this.activeAnimations.set(id, instance);

    // HARDCODE: Track broken shields for visual overlay (shield_charges delta not in state_delta)
    if (animData.animation_name === 'psionic_shield_break' && animData.target_unit_id) {
      useMapStore.getState().addBrokenShield(animData.target_unit_id);
      //console.log('[AnimationController] Shield broke for unit', animData.target_unit_id);
    }

    // VALIDATE start method exists
    if (!instance.start) {
      console.error(`[AnimationController] ❌ Animation class missing start() method`, {
        class_name: AnimationClass.name,
        animation_name: animData.animation_name,
        available_methods: Object.getOwnPropertyNames(Object.getPrototypeOf(instance)),
        error: 'All animation classes must implement start() method'
      });
      throw new Error(`[AnimationController] Animation class ${AnimationClass.name} missing start() method!`);
    }
    instance.start();

    // Controller manages destruction
    const destroyTimeout = setTimeout(() => {
      if (!this.cancelled && this.activeAnimations.has(id)) {
        //console.log(`[AnimationController] Destroying ${animData.animation_name} after ${animData.duration} ticks`);

        // VALIDATE destroy method exists
        if (!instance.destroy) {
          console.error(`[AnimationController] ❌ Animation class missing destroy() method`, {
            class_name: AnimationClass.name,
            animation_name: animData.animation_name,
            available_methods: Object.getOwnPropertyNames(Object.getPrototypeOf(instance)),
            error: 'All animation classes must implement destroy() method'
          });
          throw new Error(`[AnimationController] Animation class missing destroy() method!`);
        }
        instance.destroy();

        this.activeAnimations.delete(id);
        this.destroyTimeouts.delete(id);
      }
    }, calculateMSBasedOnTicks(animData.duration));

    this.destroyTimeouts.set(id, destroyTimeout);
  }

  /**
   * Validate animations and return detailed error information
   * @param {Array} animations - Array of animation objects to validate
   * @returns {Object} - { validAnimations: [], invalidAnimations: [] }
   */
  validateAnimations(animations) {
    const validAnimations = [];
    const invalidAnimations = [];

    animations.forEach((animation, index) => {
      const errors = [];

      // Check required fields
      if (!animation.animation_name) {
        errors.push('animation_name is missing or empty');
      }

      if (animation.duration === undefined || animation.duration === null) {
        errors.push('duration is missing (must be a number, received: ' + typeof animation.duration + ')');
      } else if (typeof animation.duration !== 'number') {
        errors.push('duration must be a number (received: ' + typeof animation.duration + ')');
      } else if (animation.duration <= 0) {
        errors.push('duration must be positive (received: ' + animation.duration + ')');
      }

      if (animation.delay === undefined || animation.delay === null) {
        errors.push('delay is missing (must be a number, received: ' + typeof animation.delay + ')');
      } else if (typeof animation.delay !== 'number') {
        errors.push('delay must be a number (received: ' + typeof animation.delay + ')');
      } else if (animation.delay < 0) {
        errors.push('delay cannot be negative (received: ' + animation.delay + ')');
      }

      // Optional but commonly used fields - warn if unusual
      if (animation.category === undefined) {
        console.debug(`[AnimationController] Animation #${index} (${animation.animation_name || 'unknown'}) has no category - will try specific or default animation`);
      }

      if (errors.length > 0) {
        invalidAnimations.push({
          animation,
          index,
          errors
        });
      } else {
        validAnimations.push(animation);
      }
    });

    return { validAnimations, invalidAnimations };
  }

  // Convert snake_case to PascalCase
  toPascalCase(str) {
    return str.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  async loadAnimationClass(animData) {
    const { animation_name, category } = animData;

    // Build possible file paths
    const pascalName = this.toPascalCase(animation_name);
    const specificPath = `./animations/${pascalName}Animation.js`;
    const categoryPath = category
      ? `./animations/Category${category.charAt(0).toUpperCase() + category.slice(1)}Animation.js`
      : null;
    const defaultPath = './DefaultAnimation.js';

    // Check registry and import the FIRST file that exists (no failed attempts!)

    // 1. Try specific animation
    if (AnimationController.animationModules[specificPath]) {
      const module = await AnimationController.animationModules[specificPath]();
      //console.debug(`[AnimationController] ✅ Loaded specific animation: ${pascalName}Animation`);
      return module.default;
    }

    // 2. Try category handler
    if (categoryPath && AnimationController.animationModules[categoryPath]) {
      const module = await AnimationController.animationModules[categoryPath]();
      //console.info(`[AnimationController] ℹ️ Using category ${category} for ${animation_name}`);
      return module.default;
    }

    // 3. Try default animation
    if (AnimationController.defaultModule[defaultPath]) {
      const module = await AnimationController.defaultModule[defaultPath]();
      console.warn(`[AnimationController] ⚠️ Using default animation for ${animation_name}`, {
        reason: 'No specific or category animation found',
        attempted_specific: specificPath,
        attempted_category: categoryPath
      });
      return module.default;
    }

    // No animation found at all
    console.error(`[AnimationController] ❌ No animation available for ${animation_name}`, {
      animation_name,
      category,
      attempted_paths: [specificPath, categoryPath, defaultPath].filter(Boolean),
      available_animations: Object.keys(AnimationController.animationModules),
      error: 'No matching animation file found in registry'
    });
    return null;
  }

  waitAndFinish() {
    const timeout = setTimeout(() => {
      if (!this.cancelled) this.finish();
    }, calculateMSBasedOnTicks(5)); // Default 5 tick wait
    this.startTimeouts.push(timeout);
  }

  finish() {
    if (this.cancelled) return;
    if (this.onComplete) {
      this.onComplete();
    }
  }

  cancel() {
    this.cancelled = true;

    // Clear all start timeouts
    this.startTimeouts.forEach(t => clearTimeout(t));
    this.startTimeouts = [];

    // Clear all destruction timeouts
    this.destroyTimeouts.forEach(t => clearTimeout(t));
    this.destroyTimeouts.clear();

    // Immediately destroy all active animations
    this.activeAnimations.forEach((instance, id) => {
      if (!instance.destroy) {
        console.error(`[AnimationController] ❌ Animation instance missing destroy() method during cancel`, {
          animation_id: id,
          instance_type: instance.constructor.name,
          available_methods: Object.getOwnPropertyNames(Object.getPrototypeOf(instance)),
          error: 'Cannot safely cancel animation without destroy() method'
        });
        throw new Error('[AnimationController] Animation instance missing destroy() method!');
      }
      instance.destroy();
    });
    this.activeAnimations.clear();
  }

  /**
   * Calculate stacking offsets for text/status animations that start at the same time
   * Groups by delay AND position for proper stacking per unit
   * Also determines if group needs to flip down (unit at top edge)
   * Mutates animations array by adding stackOffset and forceFlipDown properties
   *
   * @param {Array} animations - Array of animation objects to process
   */
  calculateStackingOffsets(animations) {
    const STACK_OFFSET_PX = 28; // Pixels between stacked popups
    const TEXT_CATEGORIES = ['text', 'status']; // Categories that use floating popups

    // Group text animations by delay AND position (same time + same unit = stack together)
    const delayGroups = new Map();

    animations.forEach((anim, index) => {
      // Only process text-based animations
      if (!TEXT_CATEGORIES.includes(anim.category)) {
        return;
      }

      // Group by delay + target position (x, y)
      const targetX = anim.target?.x ?? 0;
      const targetY = anim.target?.y ?? 0;
      const delayKey = `${anim.delay}_${targetX}_${targetY}`;

      if (!delayGroups.has(delayKey)) {
        delayGroups.set(delayKey, []);
      }
      delayGroups.get(delayKey).push({ anim, index });
    });

    // Assign stacking offsets and flip direction to groups
    delayGroups.forEach((group) => {
      // Check if this group needs to flip down (unit in first 2 rows)
      const firstAnim = group[0].anim;
      const targetY = firstAnim.target?.y ?? 0;
      // pos.y = targetY * tileSize + tileSize/2, needsFlip if pos.y < tileSize * 2
      // Simplifies to: targetY < 1.5, so rows 0 and 1 need flip
      const groupNeedsFlipDown = targetY < 2;

      // Assign flip direction to all animations in group
      // (even single animations benefit from coordinated direction)
      group.forEach((item, stackIndex) => {
        item.anim.forceFlipDown = groupNeedsFlipDown;

        // Only assign stackOffset if multiple animations in group
        if (group.length > 1) {
          // When flipping down, stack offset is positive (moves down)
          // When going up, stack offset is negative (moves up)
          const direction = groupNeedsFlipDown ? 1 : -1;
          item.anim.stackOffset = direction * stackIndex * STACK_OFFSET_PX;
        }
      });
    });
  }
}

export default AnimationController;